import {
	setActivateProfileStatus,
	setAuthorisationAccessType,
	setAuthorizationStatus,
	setShowTariffsGuestPopup,
	setStoriesMap,
} from '@/stores/slices/popupSlice';
import { Burger, Button, ButtonLink, Icon } from '@/components/ui';
import { useSelector } from 'react-redux';
import {
	getBookingAmount,
	getFavoriteCounterStatus,
	getUserTickets,
	getVideochatAmount,
	getPreviewsAmount,
} from '@/stores/slices/userSlice';
import { show, showShort } from '@/stores/slices/showFilterSlice';
import useLadyService from '@/services/LadyService';
import { getProfilesCount } from '@/stores/slices/mapModelSlice';
import {
	getBurgerMenu,
	getBurgerMenuType,
	setBurgerMenu,
} from '@/stores/slices/headerSlice';
import { LogOutButton, TopLinksTabsObj } from './index';
import { useLocation } from 'react-router-dom';
import { getProfileStatus } from '@/stores/slices/userProfileSlice';
import { getProfilesOnMapCount } from '@/stores/slices/filterSlice';

const BurgerMenu = ({ lk }) => {
	const { dispatch, userType, t, lang, navigate, userBalance } =
		useLadyService();
	const favoriteCount = useSelector(getFavoriteCounterStatus);
	const profilesCountValue = useSelector(getProfilesCount);
	const modelsOnMapCount = useSelector(getProfilesOnMapCount);
	const burgerMenuState = useSelector(getBurgerMenu);
	const userTickets = useSelector(getUserTickets);
	const videochatAmmount = useSelector(getVideochatAmount);
	const userPreviews = useSelector(getPreviewsAmount);
	const userOrders = useSelector(getBookingAmount);
	const profileStatus = useSelector(getProfileStatus);

	const location = useLocation();

	const linkByAdminAndModerator =
		userType === 'admin' || userType === 'moderator' ? 'indi' : userType;

	const closeMenu = () => {
		dispatch(setBurgerMenu(false));
	};

	const toLk = () => {
		navigate(`/lk/${linkByAdminAndModerator}`);
		closeMenu();
	};

	const showFilter = () => {
		closeMenu();
		dispatch(show());
	};

	const showShortFilter = () => {
		closeMenu();
		dispatch(showShort());
	};

	const handleActivateProfile = (e) => {
		e.preventDefault();
		dispatch(setActivateProfileStatus(true));
	};

	let defaultIcons = [
		{
			icon: 'home-main',
			name: t('mainpage'),
			link: '/',
			clazz: 'defaultIcon',
		},
		{
			icon: 'catalog',
			name: t('catalogue'),
			onClick: () => showShortFilter(),
			clazz: 'defaultIcon',
		},
		{
			icon: 'filter',
			name: t('filter'),
			onClick: () => showFilter(),
			clazz: 'defaultIcon',
		},
		{
			icon: 'map',
			name: t('onmap'),
			onClick: () => profilesCountValue && dispatch(setStoriesMap(true)),
			disabled:
				(userType === 'agency' && location.pathname.includes('lk')) ||
				modelsOnMapCount === 0 ||
				profilesCountValue === 0,
			clazz: 'defaultIcon',
		},
		{
			icon: 'favorite',
			clazz: `user__favorite${favoriteCount <= 0 ? ' empty' : ''}`,
			name: t('favorites'),
			counter: favoriteCount > 0 ? favoriteCount : null,
			link: '/history',
			clazz: 'defaultIcon',
		},
		// add about here
		{
			icon: 'erocoin-leg',
			name: t('about'),
			link: `/${lang}/about`,
			onClick: () => closeMenu(),
			clazz: 'defaultIcon',
		},
		//additional
		{
			icon: null,
			name: t('blog'),
			link: `/${lang}/blog`,
			onClick: () => closeMenu(),
			clazz: 'defaultIcon secondary-buttons',
		},
		{
			icon: null,
			name: t('contacts'),
			link: `/${lang}/about#about-portaloperator`,
			onClick: () => closeMenu(),
			clazz: 'defaultIcon secondary-buttons',
		},
		{
			icon: null,
			name: t('tariffs'),
			onClick: () => {closeMenu(); dispatch(setShowTariffsGuestPopup(true));},
			clazz: 'defaultIcon secondary-buttons',
		},
	];

	let wallet = {
		name: t('balance'),
		link: '/lk/balance',
		wallet: userBalance > 0 ? userBalance : null,
		icon: 'wallet',
	};

	let support = {
		icon: 'operator',
		name: t('support'),
		link: '/lk/support',
		counter: userTickets > 0 ? userTickets : null,
	};

	let instruction = {
		icon: 'instruction-cont',
		name: t('guide'),
		link: `/guide`,
	};

	let photographer = {
		icon: 'camera',
		name: t('photographer'),
		link: `/${lang}/lk/photograph`,
	};

	let videochat = {
		icon: 'video',
		name: t('videochat'),
		link: `/lk/videochats`,
		counter: videochatAmmount > 0 ? videochatAmmount : null,
	};

	let statistic = {
		icon: 'stat',
		name: t('mystat'),
		link: `/lk/statistic`,
	};

	let reviews = {
		icon: 'star',
		name: t('reviews'),
		link: `/lk/reviews`,
	};

	let profiles = {
		icon: 'user-id',
		name: t('myads'),
		link: `/lk/profiles`,
		disabled: !profileStatus,
		onClick: profileStatus ? null : handleActivateProfile,
	};

	let orders = {
		icon: 'pay-content',
		name: t('myorder'),
		link: `/lk/reservations`,
		counter: userOrders > 0 ? userOrders : null,
	};

	const headerIcons = {
		indi: [
			{
				icon: 'person',
				name: t('profuser'),
				clazz: 'authorised_user big',
				onClick: () => toLk(),
			},
			{
				icon: profiles.icon,
				name: profiles.name,
				clazz: `big ${profiles.disabled ? 'button-disabled' : ''}`,
				link: profiles.link,
				onClick: profiles.onClick,
			},
			{
				name: wallet.name,
				icon: wallet.icon,
				link: wallet.link,
				wallet: wallet.wallet,
				clazz: 'big',
			},
			videochat,
			orders,
			reviews,
			statistic,
			support,
			instruction,
		],
		admin: [profiles, wallet],
		agency: [
			{
				icon: 'person',
				name: t('profuser'),
				clazz: 'authorised_user big',
				onClick: () => toLk(),
			},
			{
				icon: profiles.icon,
				name: profiles.name,
				clazz: `big ${profiles.disabled ? 'button-disabled' : ''}`,
				link: profiles.link,
				onClick: profiles.onClick,
			},
			{
				name: wallet.name,
				icon: wallet.icon,
				link: wallet.link,
				wallet: wallet.wallet,
				clazz: 'big',
			},
			videochat,
			orders,
			{
				icon: 'casting',
				name: t('preview'),
				counter: userPreviews > 0 ? userPreviews : null,
				link: 'lk/previews',
			},
			reviews,
			{
				icon: 'operator',
				name: t('myoperator'),
				link: `/${lang}/lk/operators`,
				counter: userTickets > 0 ? userTickets : null,
			},
			{
				icon: 'party',
				name: t('myparty'),
				link: 'lk/parties',
			},
			statistic,
			instruction,
		],
		customer_photographer: [
			{
				icon: 'person',
				name: t('profuser'),
				clazz: 'authorised_user',
				onClick: () => toLk(),
			},
			{
				icon: 'filter2',
				name: t('myfilters'),
				link: `/lk/${linkByAdminAndModerator}/subscriptions`,
			},
			{
				icon: 'time',
				name: t('history'),
				link: `/history`,
			},

			wallet,

			instruction,

			support,
		],
		default: [
			{
				icon: 'person',
				name: t('login'),
				clazz: 'default big',
				onClick: () => {
					dispatch(setAuthorizationStatus(true));
				},
			},
			{...instruction, clazz: ' big',},
			{
				icon: 'operator',
				name: t('support'),
				clazz: ' big',
				onClick: () => {
					dispatch(setAuthorisationAccessType('support'));
					dispatch(setAuthorizationStatus(true));
				},
			},
		],
	};

	const iconsByUserType = () => {
		switch (userType) {
			case 'indi':
				return headerIcons.indi;

			case 'admin':
			case 'moderator':
				return headerIcons.admin;

			case 'agency':
				return headerIcons.agency;
			case 'default':
				return headerIcons.default;
			default:
				return headerIcons.customer_photographer;
		}
	};

	let linksByType = () => {
		switch (userType) {
			case 'customer':
				return TopLinksTabsObj().customer;
			case 'photograph':
				return TopLinksTabsObj().photograph;
			default:
				return TopLinksTabsObj().default;
		}
	};

	const burgerMenuTypeDefault = useSelector(getBurgerMenuType);

	let buttonTemplate = ({ i, index }) => {
		let propses = {
			title: i.name,
			size: 'l',
			href: i.link,
			onClick: i.onClick ? i.onClick : () => closeMenu(),
			disabled: i.disabled,
			clazz: `menu__link ${i.clazz ? ` ${i.clazz}` : ''}`,
		};

		let propsesIcon = {
			counter: i.wallet ? null : i.counter,
			size: 'xl',
			clazz: i.clazz,
			spritePath: i.icon,
		};

		if (i.link) {
			return (
				<ButtonLink
					key={index + 1}
					{...propses}
				>
					{i.icon === 'wallet' ? (
						<span className={'menu__wallet currency--erocoin'}>
							{i.wallet}
						</span>
					) : (i.icon &&
						<Icon {...propsesIcon} />
					)}

					<span>{i.name}</span>
				</ButtonLink>
			);
		} else {
			return (
				<Button
					key={index + 1}
					{...propses}
				>
					<Icon {...propsesIcon} />
					<span>{i.name}</span>
				</Button>
			);
		}
	};

	return (
		<div className={`users${!!burgerMenuState ? ' _active' : ''}`}>
			<Burger />

			{burgerMenuTypeDefault && (
				<>
					<div className={'users__nav top-nav'}>
						{defaultIcons.map((i, index) =>
							buttonTemplate({ i, index }),
						)}
					</div>

					<hr />
				</>
			)}

			<p className={'color-green'}>{t('lk')}</p>

			<div className={'users__nav'}>
				{iconsByUserType().map((i, index) =>
					buttonTemplate({ i, index }),
				)}
			</div>

			{/* {userType === "default" ? (
        <div className="users__links">
          {linksByType().map((i, index) => {
            if (i.name === t("support") && userType === "default") {
              return (
                <ButtonLink
                  onClick={() => {
                    dispatch(setAuthorisationAccessType("support"));
                    dispatch(setAuthorizationStatus(true));
                  }}
                  href={i.link}
                  title={i.name}
                  key={index + 1}
                  children={i.name}
                  clazz={"hover-line"}
                >
                  {i.name}
                </ButtonLink>
              );
            }

            return (
              <ButtonLink
                onClick={() => closeMenu()}
                href={i.href}
                title={i.name}
                key={index + 1}
                children={i.name}
                clazz={"hover-line"}
              />
            );
          })}
        </div>
      ) : null} */}

			<div className="users__bottom">
				{/* BACK TO WEB */}
				{/* {lk && (
          <ButtonLink
            title={t("backtoweb")}
            href={"/"}
            onClick={closeMenu}
            size={"m"}
            clazz={"button--tetriary td-none w-100"}
          >
            {t("backtoweb")}
          </ButtonLink>
        )} */}
				{userType === 'default' && (
					<>
						{/* <Button
							clazz={'button button_outline--green w-100'}
							onClick={() => {
								closeMenu();
								dispatch(setShowTariffsGuestPopup(true));
							}}
						>
							<Icon
								style={{ fontSize: '16px' }}
								spritePath={'erocoin'}
							/>

							{t('tariffs')}
						</Button> */}

						<ButtonLink
							onClick={() => closeMenu()}
							href={`/${lang}/registration#indi`}
							title={t('add')}
							size={'xs'}
							clazz={'button--green hover w-100'}
						>
							<Icon
								size={'l'}
								spritePath={'plus'}
							/>
							{t('add')}
						</ButtonLink>
					</>
				)}
				{/* {(userType === 'indi' || userType === 'agency') && (
					<Button
						clazz={'button button_outline--green w-100'}
						onClick={() => {
							closeMenu();
							dispatch(setShowTariffsGuestPopup(true));
						}}
					>
						<Icon
							style={{ fontSize: '16px' }}
							spritePath={'erocoin'}
						/>

						{t('tariffs')}
					</Button>
				)} */}
				{userType !== 'default' && <LogOutButton size={'m'} />}
			</div>
		</div>
	);
};

export default BurgerMenu;
